import type { ReactNode } from 'react';
import { revealStyle, useScrollReveal } from '@/hooks/useScrollReveal';
import { cn } from '@/lib/utils';

/**
 * Real Screen (spec 010 §4 D): one big real-looking surface, two or three callouts, an
 * `Example ...` caption. Used by /what-we-do/sat/about in three different compositions so the
 * page does not repeat one template:
 *   - `surface-left`  surface wide on the left, text + callouts on the right
 *   - `surface-right` text + callouts on the left, surface on the right
 *   - `stacked`       title and lead side by side, the surface full width, callouts in a row below
 *
 * The callout numbers match the pins drawn inside the surface (`<CalloutPin n={1} />`), so the
 * numbering is a real key, not decoration. Scroll-reveal fires once, via the shared hook.
 */

export interface Callout {
  title: string;
  body: string;
}

export type RealScreenLayout = 'surface-left' | 'surface-right' | 'stacked';

/** A numbered pin placed inside a surface. Decorative: the callout list carries the text. */
export function CalloutPin({ n, className }: { n: number; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold leading-none text-primary-foreground shadow-sm ring-2 ring-card',
        className,
      )}
    >
      {n}
    </span>
  );
}

/** Window chrome around an example surface. Static: nothing inside is interactive. */
export function SurfaceFrame({
  url,
  children,
  className,
}: {
  url: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border bg-card shadow-[0_28px_70px_-32px_hsl(345_75%_31%/0.45)] dark:shadow-[0_28px_70px_-32px_hsl(0_0%_0%/0.8)]',
        className,
      )}
    >
      <div className="flex items-center gap-3 border-b bg-muted px-4 py-2.5" aria-hidden>
        <span className="flex shrink-0 gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
        </span>
        <span className="mx-auto max-w-[70%] truncate rounded-md border bg-background px-3 py-0.5 text-[11px] text-muted-foreground">
          {url}
        </span>
        <span className="w-9 shrink-0" />
      </div>
      {children}
    </div>
  );
}

interface SatRealScreenProps {
  id: string;
  title: ReactNode;
  lead: string;
  callouts: readonly Callout[];
  /** Always starts with `Example`. */
  caption: string;
  layout: RealScreenLayout;
  /** Section background classes (kept per use so neighbouring sections alternate). */
  tone?: string;
  /** Extra content under the callouts (a link, for example). */
  footer?: ReactNode;
  children: ReactNode;
}

export function SatRealScreen({ id, title, lead, callouts, caption, layout, tone, footer, children }: SatRealScreenProps) {
  const { ref, isVisible } = useScrollReveal(0.1);
  const titleId = `${id}-title`;

  const calloutList = (
    <ol
      className={cn(
        'grid gap-5',
        layout === 'stacked' ? 'md:grid-cols-3 md:gap-8' : 'lg:gap-6',
      )}
      data-testid={`${id}-callouts`}
    >
      {callouts.map((c, i) => (
        <li key={c.title} className="flex gap-3">
          <CalloutPin n={i + 1} className="mt-0.5 !ring-0" />
          <div>
            <p className="font-semibold leading-snug">{c.title}</p>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
          </div>
        </li>
      ))}
    </ol>
  );

  const header = (
    <header className={cn(layout === 'stacked' && 'lg:grid lg:grid-cols-2 lg:items-end lg:gap-16')}>
      <h2
        id={titleId}
        className={cn('text-balance text-3xl font-bold tracking-tight sm:text-4xl', layout === 'stacked' ? 'max-w-[24ch]' : 'max-w-[18ch]')}
      >
        {title}
      </h2>
      <p className={cn('mt-4 max-w-[46ch] text-base leading-relaxed text-muted-foreground sm:text-lg', layout === 'stacked' && 'lg:mt-0')}>
        {lead}
      </p>
    </header>
  );

  const figure = (
    <div ref={ref} style={revealStyle(isVisible)} className="min-w-0">
      <figure data-testid={`${id}-screen`}>
        {children}
        <figcaption className="mt-3 text-sm text-muted-foreground">{caption}</figcaption>
      </figure>
    </div>
  );

  if (layout === 'stacked') {
    return (
      <section id={id} aria-labelledby={titleId} className={cn('scroll-mt-20', tone)}>
        <div className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 lg:py-20">
          {header}
          <div className="mt-9">{figure}</div>
          <div className="mt-9">{calloutList}</div>
        </div>
      </section>
    );
  }

  const left = layout === 'surface-left';
  return (
    <section id={id} aria-labelledby={titleId} className={cn('scroll-mt-20', tone)}>
      <div
        className={cn(
          'mx-auto grid w-full max-w-6xl gap-x-14 gap-y-8 px-4 py-14 sm:px-6 lg:grid-rows-[auto_1fr] lg:py-20',
          left ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,21rem)]' : 'lg:grid-cols-[minmax(0,21rem)_minmax(0,1fr)]',
        )}
      >
        <div className={cn(left ? 'lg:col-start-2 lg:row-start-1' : 'lg:col-start-1 lg:row-start-1')}>{header}</div>
        <div className={cn('lg:row-span-2 lg:row-start-1', left ? 'lg:col-start-1' : 'lg:col-start-2')}>{figure}</div>
        <div className={cn('space-y-6 lg:row-start-2 lg:self-start', left ? 'lg:col-start-2' : 'lg:col-start-1')}>
          {calloutList}
          {footer}
        </div>
      </div>
    </section>
  );
}
